import { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { KaraokeAuthContext } from '~/modules/Auth/KaraokeAuthContext';
import { KaraokeAuthError } from '~/modules/Auth/KaraokeAuthError';
import type { KaraokeAuthContextValue, KaraokeAuthStatus, KaraokeAuthUser } from '~/modules/Auth/KaraokeAuthTypes';

const AUTH_TOKEN_STORAGE_KEY = 'fastkaraoke.authToken';
const API_BASE = (import.meta.env.VITE_KARAOKE_API_BASE ?? '/api/karaoke').replace(/\/$/, '');

interface AuthResponse {
  token?: string;
  user: KaraokeAuthUser;
}

interface ApiErrorPayload {
  message?: string;
  errors?: Record<string, string[]>;
}

const guestUser: KaraokeAuthUser = {
  type: 'guest',
  id: null,
  name: 'Guest',
  email: null,
};

function getStoredToken() {
  try {
    return window.localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

function storeToken(token: string | null) {
  try {
    if (token) {
      window.localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, token);
    } else {
      window.localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
    }
  } catch {
    // Browsers can block storage in strict/privacy modes; auth still works for the current page lifetime.
  }
}

async function parseResponseError(response: Response): Promise<KaraokeAuthError> {
  const payload = (await response.json().catch(() => null)) as ApiErrorPayload | null;
  const firstValidationError = payload?.errors ? Object.values(payload.errors).flat()[0] : null;

  return new KaraokeAuthError(
    payload?.message ?? firstValidationError ?? 'Authentication request failed.',
    payload?.errors ?? {},
  );
}

async function authRequest<T>(path: string, options: RequestInit = {}, token?: string | null): Promise<T> {
  const headers = new Headers(options.headers);
  headers.set('Accept', 'application/json');

  if (options.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    throw await parseResponseError(response);
  }

  return (await response.json()) as T;
}

export function KaraokeAuthProvider({ children }: PropsWithChildren) {
  const [status, setStatus] = useState<KaraokeAuthStatus>('loading');
  const [user, setUser] = useState<KaraokeAuthUser | null>(null);
  const [token, setToken] = useState<string | null>(() => getStoredToken());

  const applySession = useCallback((response: AuthResponse) => {
    if (response.token !== undefined) {
      storeToken(response.token);
      setToken(response.token);
    }

    setUser(response.user);
    setStatus(response.user.type === 'user' ? 'authenticated' : 'guest');
  }, []);

  const startGuestSession = useCallback(async () => {
    try {
      const response = await authRequest<AuthResponse>('/auth/guest', { method: 'POST' });
      applySession(response);
    } catch {
      storeToken(null);
      setToken(null);
      setUser(guestUser);
      setStatus('guest');
    }
  }, [applySession]);

  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      const storedToken = getStoredToken();

      if (storedToken) {
        try {
          const response = await authRequest<AuthResponse>('/auth/me', { method: 'GET' }, storedToken);
          if (!cancelled) {
            applySession({ ...response, token: storedToken });
          }
          return;
        } catch {
          storeToken(null);
        }
      }

      if (!cancelled) {
        await startGuestSession();
      }
    };

    void bootstrap();

    return () => {
      cancelled = true;
    };
  }, [applySession, startGuestSession]);

  const login = useCallback(
    async (email: string, password: string) => {
      const response = await authRequest<AuthResponse>('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });
      applySession(response);
    },
    [applySession],
  );

  const register = useCallback(
    async (email: string, password: string, passwordConfirmation: string) => {
      const response = await authRequest<AuthResponse>('/auth/register', {
        method: 'POST',
        body: JSON.stringify({ email, password, password_confirmation: passwordConfirmation }),
      });
      applySession(response);
    },
    [applySession],
  );

  const logout = useCallback(async () => {
    try {
      const response = await authRequest<AuthResponse>('/auth/logout', { method: 'POST' }, token);
      applySession(response);
    } catch {
      storeToken(null);
      setToken(null);
      setUser(guestUser);
      setStatus('guest');
    }
  }, [applySession, token]);

  const value = useMemo<KaraokeAuthContextValue>(
    () => ({
      status,
      user,
      isGuest: status !== 'authenticated',
      isAuthenticated: status === 'authenticated',
      login,
      register,
      logout,
    }),
    [login, logout, register, status, user],
  );

  return <KaraokeAuthContext.Provider value={value}>{children}</KaraokeAuthContext.Provider>;
}
